import { Button, InlineNotification, Stack, Tag, TextInput } from '@carbon/react';
import { Launch, Renew, View, ViewOff } from '@carbon/icons-react';
import {
  asProblem,
  FieldInput,
  FormModal,
  hasPermission,
  ListTable,
  problemMessage,
  useAdmin,
  useFormat,
  type RecordValues,
} from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ErrorNotice } from '../common/Notices';

async function unwrap<T>(call: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T | null> {
  const { data, error, response } = await call;
  if (!response.ok) throw error ?? { status: response.status };
  return data ?? null;
}

const versionsKey = (id: string) => ['cms', 'page-versions', id] as const;

/**
 * Putting a page on the website (ADR 0081): publish its draft now or between two times, with a note, or take it down. Both need
 * the publish permission and are audited; the draft stays as it is. *Preview* (CMS phase 2, with the edit permission) opens the
 * saved draft on the website through a link that works for an hour.
 */
export function PagePublishActions({ page }: { page: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const queryClient = useQueryClient();
  const id = String(page.id);
  const [open, setOpen] = useState(false);
  const [schedule, setSchedule] = useState<{ publishAt: string | null; unpublishAt: string | null; note: string }>({
    publishAt: null,
    unpublishAt: null,
    note: '',
  });
  const refresh = () => {
    void queryClient.invalidateQueries();
  };
  const publish = useMutation({
    mutationFn: () =>
      unwrap(
        hoApi.POST('/api/v1/cms/pages/{id}/publish', {
          params: { path: { id } },
          body: { publishAt: schedule.publishAt, unpublishAt: schedule.unpublishAt, note: schedule.note || null },
        }),
      ),
    onSuccess: () => {
      setOpen(false);
      refresh();
    },
  });
  const unpublish = useMutation({
    mutationFn: () => unwrap(hoApi.POST('/api/v1/cms/pages/{id}/unpublish', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const preview = useMutation({
    mutationFn: () => unwrap(hoApi.POST('/api/v1/cms/pages/{id}/preview', { params: { path: { id } } })),
    onSuccess: (link) => {
      if (link) globalThis.open(link.url, '_blank', 'noopener');
    },
  });
  const canPreview = page.kind !== 'site' && hasPermission(session, 'ho.content.manage');
  const canPublish = hasPermission(session, 'ho.content.publish');
  if (!canPreview && !canPublish) return null;

  return (
    <>
      {canPreview && (
        <Button kind="tertiary" renderIcon={View} disabled={preview.isPending} onClick={() => preview.mutate()}>
          {t('cms.preview.button')}
        </Button>
      )}
      {preview.data && (
        <InlineNotification
          kind="info"
          lowContrast
          role="status"
          title={t('cms.preview.ready', { until: format.dateTime(preview.data.expiresAt) })}
          subtitle={preview.data.url}
          onClose={() => preview.reset()}
        />
      )}
      {preview.error && <ErrorNotice title={problemMessage(t, asProblem(preview.error))} />}
      {canPublish && (
        <Button
          kind="primary"
          renderIcon={Launch}
          onClick={() => {
            publish.reset();
            setOpen(true);
          }}
        >
          {t('cms.publish.button')}
        </Button>
      )}
      {canPublish && page.status === 'published' && (
        <Button kind="tertiary" renderIcon={ViewOff} disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>
          {t('cms.publish.unpublish')}
        </Button>
      )}
      {unpublish.error && <ErrorNotice title={problemMessage(t, asProblem(unpublish.error))} />}
      {open && (
        <FormModal
          title={t('cms.publish.title')}
          primaryText={t('cms.publish.confirm')}
          pending={publish.isPending}
          error={publish.error ? problemMessage(t, asProblem(publish.error)) : null}
          onClose={() => setOpen(false)}
          onSubmit={() => publish.mutate()}
        >
          <Stack gap={5}>
            <p>{t('cms.publish.hint')}</p>
            <FieldInput
              field={{ key: 'publish-at', label: 'cms.publish.from', type: 'datetime', helper: 'cms.publish.fromHelp' }}
              value={schedule.publishAt}
              onChange={(v) => setSchedule((w) => ({ ...w, publishAt: (v as string | null) ?? null }))}
            />
            <FieldInput
              field={{
                key: 'unpublish-at',
                label: 'cms.publish.until',
                type: 'datetime',
                helper: 'cms.publish.untilHelp',
              }}
              value={schedule.unpublishAt}
              onChange={(v) => setSchedule((w) => ({ ...w, unpublishAt: (v as string | null) ?? null }))}
            />
            <TextInput
              id="publish-note"
              labelText={t('cms.publish.note')}
              maxLength={300}
              value={schedule.note}
              onChange={(e) => setSchedule((w) => ({ ...w, note: e.target.value }))}
            />
          </Stack>
        </FormModal>
      )}
    </>
  );
}

/** A page's published versions, newest first, which one the website shows, and making an earlier one the draft again. */
export function PageVersionsTab({ page }: { page: RecordValues }) {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const queryClient = useQueryClient();
  const id = String(page.id);
  const versions = useQuery({
    queryKey: versionsKey(id),
    queryFn: () => unwrap(hoApi.GET('/api/v1/cms/pages/{id}/versions', { params: { path: { id } } })),
  });
  const restore = useMutation({
    mutationFn: (versionNo: number) =>
      unwrap(
        hoApi.POST('/api/v1/cms/pages/{id}/versions/{versionNo}/restore', { params: { path: { id, versionNo } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries(),
  });
  const canPublish = hasPermission(session, 'ho.content.publish');
  const rows = (versions.data ?? []).map((v) => ({ ...v, id: String(v.versionNo) }));
  return (
    <Stack gap={5}>
      {page.hasChanges === true && page.status === 'published' && (
        <InlineNotification kind="info" lowContrast hideCloseButton title={t('cms.versions.changes')} />
      )}
      {restore.isSuccess && (
        <InlineNotification kind="success" lowContrast hideCloseButton title={t('cms.versions.restored')} />
      )}
      {restore.error && <ErrorNotice title={problemMessage(t, asProblem(restore.error))} />}
      <ListTable<(typeof rows)[number]>
        ariaLabel={t('cms.versions.title')}
        loading={versions.isLoading}
        error={versions.error ? problemMessage(t, asProblem(versions.error)) : undefined}
        onRetry={() => void versions.refetch()}
        rows={rows}
        empty={{ heading: t('cms.versions.empty') }}
        columns={[
          {
            key: 'versionNo',
            header: t('cms.versions.version'),
            render: (v) => (
              <>
                {v.versionNo} {v.isOnWebsite && <Tag type="green">{t('cms.versions.onWebsite')}</Tag>}
              </>
            ),
          },
          { key: 'publishedAt', header: t('cms.versions.publishedAt'), render: (v) => format.dateTime(v.publishedAt) },
          {
            key: 'window',
            header: t('cms.versions.window'),
            render: (v) =>
              [v.publishAt ? format.dateTime(v.publishAt) : '', v.unpublishAt ? format.dateTime(v.unpublishAt) : '']
                .filter(Boolean)
                .join(' → '),
          },
          { key: 'note', header: t('cms.versions.note'), render: (v) => v.note ?? '' },
          {
            key: 'restore',
            header: '',
            render: (v) =>
              canPublish ? (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={Renew}
                  disabled={restore.isPending}
                  onClick={() => restore.mutate(Number(v.versionNo))}
                >
                  {t('cms.versions.restore')}
                </Button>
              ) : null,
          },
        ]}
      />
    </Stack>
  );
}
